import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { CATEGORIES, CATEGORIES_SORTED, categoryTier } from '@/data/categories';
import { Heart, Shield, Users, Globe, Sun, Moon, Eye, ChevronRight, ArrowRight, Zap, ScrollText, FileText } from 'lucide-react';

function catSlug(name) {
  return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import TncBody from '@/components/TncBody';
import WorldMap from '@/components/WorldMap';
import TicketSupport from '@/components/TicketSupport';

// Editorial portrait illustrations — professional Chinese-Indonesian styled.
// AI-generated, hosted on Emergent CDN. Rendered as <img> to fill the card box.
const WOMAN_IMG = 'https://static.prod-images.emergentagent.com/jobs/89a1b5b9-2080-44e2-9f5e-2d062c3e298d/images/26afdf1870838869e39f0afc938aea735b6b2b6bcbb6c65cfaf3ba2706656334.jpeg';
const MAN_IMG = 'https://static.prod-images.emergentagent.com/jobs/89a1b5b9-2080-44e2-9f5e-2d062c3e298d/images/5c316b4ee2299cf5ac530169e62b59ec3aa1830e20df809141af87636dcd862c.jpeg';

const WOMAN_SVG = (
  <img src={WOMAN_IMG} alt="Chinese-Indonesian woman portrait illustration" loading="lazy" className="w-full h-full object-cover block" />
);

const MAN_SVG = (
  <img src={MAN_IMG} alt="Chinese-Indonesian man portrait illustration" loading="lazy" className="w-full h-full object-cover block" />
);

const STATS = [
  { num: '18', label: 'Categories', labelId: 'Kategori' },
  { num: '7+', label: 'Countries', labelId: 'Negara' },
];

const TOP_COUNTRIES = [
  { code: 'id', name: 'Indonesia' },
  { code: 'au', name: 'Australia' },
  { code: 'us', name: 'USA' },
  { code: 'kr', name: 'South Korea' },
  { code: 'tw', name: 'Taiwan' },
  { code: 'cn', name: 'China' },
  { code: 'sg', name: 'Singapore' },
  { code: 'jp', name: 'Japan' },
  { code: 'my', name: 'Malaysia' },
  { code: 'kh', name: 'Cambodia' },
  { code: 'tl', name: 'Timor Leste' },
  { code: 'ae', name: 'UAE' },
  { code: 'kw', name: 'Kuwait' },
];

const FEATURES_DATA = {
  en: [
    { icon: Eye, title: 'Browse & Filter', desc: 'Find someone who fits — filter by city, category, education, and more. Every search brings you closer to a genuine connection.' },
    { icon: Users, title: '18 Unique Categories', desc: 'From faith-based to location-based communities across Indonesia and beyond.' },
    { icon: Zap, title: 'Instant Connections', desc: 'Direct WhatsApp and Instagram links to connect with your match instantly.' },
  ],
  id: [
    { icon: Eye, title: 'Jelajahi & Filter', desc: 'Temukan yang sepadan — pilih berdasarkan kota, kategori, dan pendidikan. Setiap pencarian mendekatkanmu pada koneksi yang tulus.' },
    { icon: Users, title: '18 Kategori Unik', desc: 'Dari komunitas berbasis iman hingga lokasi di seluruh Indonesia dan luar negeri.' },
    { icon: Zap, title: 'Koneksi Instan', desc: 'Link langsung WhatsApp dan Instagram untuk terhubung dengan pasanganmu.' },
  ],
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, lang, toggleLang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [hoveredCat, setHoveredCat] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [showTnc, setShowTnc] = useState(false);
  const features = FEATURES_DATA[lang] || FEATURES_DATA.en;

  return (
    <div className="min-h-screen t-bg overflow-hidden">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-shrink-0">
            <img src="/images/mgi-logo.jpeg" alt="MGI" className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-contain" />
            <span className="text-sm sm:text-xl font-bold t-text whitespace-nowrap">MGI<span className="hidden sm:inline"> - Match Group </span><span className="gradient-text hidden sm:inline">ID</span></span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={toggleTheme} data-testid="landing-theme-toggle" className="p-1.5 sm:p-2 rounded-full glass t-text-secondary hover:opacity-80 transition">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={toggleLang} data-testid="language-toggle" className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full glass text-xs sm:text-sm t-text-secondary hover:opacity-80 transition">
              <Globe className="w-3 sm:w-3.5 h-3 sm:h-3.5" />{lang === 'en' ? 'ID' : 'EN'}
            </button>
            <Button data-testid="nav-login-btn" onClick={() => navigate('/auth')} variant="ghost" className="t-text-secondary rounded-full hover:opacity-80 text-xs sm:text-sm px-2 sm:px-4">{t('login')}</Button>
            <Button data-testid="nav-admin-btn" onClick={() => navigate('/admin')} variant="ghost" className="t-text-muted rounded-full text-xs sm:text-sm hover:opacity-80 hidden sm:inline-flex">Admin</Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background accents */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-[10%] w-[500px] h-[500px] rounded-full bg-[#D98A94]/8 blur-[150px]" />
          <div className="absolute bottom-1/4 right-[10%] w-[400px] h-[400px] rounded-full bg-[#C7867A]/8 blur-[130px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-32 pb-16 sm:pb-24 grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          {/* Left - Text */}
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <h1 data-testid="hero-tagline" className="text-4xl sm:text-6xl lg:text-7xl font-bold t-text tracking-tight leading-[1.05] mb-6 sm:mb-8 font-romantic mt-6 sm:mt-8">
              {lang === 'en' ? (
                <>
                  <span className="italic font-normal t-text-secondary block text-2xl sm:text-3xl mb-2 tracking-wide">Begin your</span>
                  <span>Shared </span>
                  <span className="gradient-text">Journey.</span>
                </>
              ) : (
                <>
                  <span className="italic font-normal t-text-secondary block text-2xl sm:text-3xl mb-2 tracking-wide">Cerita kita</span>
                  <span>Dimulai </span>
                  <span className="gradient-text">di Sini.</span>
                </>
              )}
            </h1>

            <p className="text-base sm:text-lg t-text-secondary max-w-lg mb-8 sm:mb-10 font-body leading-relaxed">
              {lang === 'en'
                ? 'Meet someone who understands your roots, your faith, and the life you dream of building. Across 18 heartfelt communities — faith, culture, and everything in between.'
                : 'Bertemu dengan seseorang yang memahami akar budaya, imanmu, dan mimpi hidupmu. Di 18 komunitas hangat — iman, budaya, dan segala hal di antaranya.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12">
              <Button data-testid="hero-get-started-btn" onClick={() => navigate('/auth')}
                className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full px-8 sm:px-10 py-4 sm:py-6 text-base sm:text-lg font-medium btn-glow active:scale-95" size="lg">
                {t('get_started')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button data-testid="hero-admin-btn" onClick={() => navigate('/admin')}
                variant="outline" className="rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg t-text-secondary t-border hover:opacity-80" size="lg">
                Admin Portal
              </Button>
            </div>

            {/* Quick stats */}
            <div className="flex items-center gap-6 sm:gap-10">
              {STATS.map((s, i) => (
                <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.1 }}>
                  <p className="text-2xl font-bold gradient-text">{s.num}</p>
                  <p className="text-xs t-text-muted uppercase tracking-wider">{lang === 'en' ? s.label : s.labelId}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Woman & Man illustration cards */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="hidden lg:flex justify-center items-center relative gap-4">
            {/* Woman card */}
            <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl w-56 h-72" style={{ filter: 'drop-shadow(0 20px 60px rgba(217, 138, 148, 0.15))' }} data-testid="hero-woman">
                {WOMAN_SVG}
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                {['Buddhist', 'PIK', 'Uwiw', 'Tenglang'].map(tag => (
                  <span key={tag} className="text-[9px] px-2 py-0.5 rounded-full bg-white/90 text-[#D98A94] font-semibold shadow-sm">{tag}</span>
                ))}
              </div>
            </motion.div>
            {/* Man card */}
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="relative mt-12">
              <div className="rounded-3xl overflow-hidden shadow-2xl w-56 h-72" style={{ filter: 'drop-shadow(0 20px 60px rgba(199, 134, 122, 0.15))' }} data-testid="hero-man">
                {MAN_SVG}
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                {['Christian', 'BSD', 'Taiwan'].map(tag => (
                  <span key={tag} className="text-[9px] px-2 py-0.5 rounded-full bg-white/90 text-[#C7867A] font-semibold shadow-sm">{tag}</span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <div className="w-6 h-10 rounded-full border-2 t-border flex items-start justify-center p-1">
            <div className="w-1.5 h-3 rounded-full bg-[#D98A94]" />
          </div>
        </motion.div>
      </section>

      {/* Global Presence — editorial marquee */}
      <section className="py-14 sm:py-24 px-4 sm:px-6 relative overflow-hidden" style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        {/* Ambient background glow */}
        <div className="absolute inset-0 pointer-events-none opacity-60" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(217,138,148,0.10), transparent 55%)' }} />
        <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-[#D98A94]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-[#C7867A]/10 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative">
          {/* Editorial header */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-14">
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-[#D98A94] font-semibold">
              {lang === 'en' ? 'Global Presence' : 'Kehadiran Global'}
            </span>
            <h2 className="text-3xl sm:text-5xl font-bold t-text mt-3 tracking-tight font-romantic">
              {lang === 'en' ? 'Members Across 13+ Countries' : 'Anggota di 13+ Negara'}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" aria-hidden="true">
              <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#D98A94]/50" />
              <Globe className="w-4 h-4 text-[#D98A94]" />
              <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#D98A94]/50" />
            </div>
            <p className="t-text-secondary text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
              {lang === 'en'
                ? 'From Jakarta to Dubai — one platform connecting hearts across cultures, faiths, and continents.'
                : 'Dari Jakarta hingga Dubai — satu platform yang menyatukan hati lintas budaya, iman, dan benua.'}
            </p>
          </motion.div>

          {/* Auto-scrolling marquee strip */}
          <div className="relative">
            {/* Edge fade masks */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 z-10" style={{ background: 'linear-gradient(to right, var(--bg-color, #FAF8F5), transparent)' }} />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 z-10" style={{ background: 'linear-gradient(to left, var(--bg-color, #FAF8F5), transparent)' }} />

            <div className="overflow-hidden" data-testid="countries-marquee">
              <div className="flex gap-3 sm:gap-4 mgi-marquee-track" style={{ width: 'max-content' }}>
                {[...TOP_COUNTRIES, ...TOP_COUNTRIES].map((c, i) => {
                  const leftHoist = ['ae', 'kw'].includes(c.code);
                  const objPos = leftHoist ? { objectPosition: '18% center' } : undefined;
                  return (
                    <div key={`${c.code}-${i}`}
                      className="flex items-center gap-3 pl-2 pr-5 py-2 rounded-full glass-card flex-shrink-0 hover:scale-105 hover:border-[#D98A94] transition-all cursor-default"
                      style={{ minWidth: 'fit-content' }}>
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white shadow-md">
                          <img src={`https://flagcdn.com/w80/${c.code}.png`} alt={c.name} loading="lazy" style={objPos} className="w-full h-full object-cover" />
                        </div>
                        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full mgi-live-dot" aria-hidden="true" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold t-text whitespace-nowrap tracking-wide">{c.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Live-dot legend */}
          <div className="flex items-center justify-center gap-2 mt-6 sm:mt-8">
            <span className="w-2 h-2 rounded-full bg-green-500 mgi-live-dot" />
            <span className="text-[11px] sm:text-xs t-text-muted tracking-wide">
              {lang === 'en' ? 'Live members active right now' : 'Member aktif saat ini'}
            </span>
          </div>
        </div>

        <style>{`
          .mgi-marquee-track { animation: mgi-marquee-scroll 45s linear infinite; }
          .mgi-marquee-track:hover { animation-play-state: paused; }
          @keyframes mgi-marquee-scroll {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .mgi-live-dot { animation: mgi-live-pulse 2s ease-in-out infinite; }
          @keyframes mgi-live-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
            50%      { box-shadow: 0 0 0 6px rgba(34,197,94,0);    }
          }
          @media (prefers-reduced-motion: reduce) {
            .mgi-marquee-track { animation: none; }
            .mgi-live-dot { animation: none; }
          }
        `}</style>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-32 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#D98A94]/[0.02]" />
        {/* Decorative floating hearts (very subtle) */}
        <div className="absolute top-10 left-10 text-[#D98A94]/[0.06] text-8xl select-none pointer-events-none">❦</div>
        <div className="absolute bottom-10 right-16 text-[#C7867A]/[0.06] text-9xl select-none pointer-events-none">❦</div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-16">
            <span className="text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#D98A94] font-medium">{lang === 'en' ? 'Why Choose Us' : 'Mengapa Memilih Kami'}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold t-text mt-3 sm:mt-4 tracking-tight font-romantic">{t('features')}</h2>
            {/* Ornate divider */}
            <div className="flex items-center justify-center gap-3 mt-4 sm:mt-6" aria-hidden="true">
              <span className="h-px w-10 sm:w-16 bg-gradient-to-r from-transparent to-[#D98A94]/50" />
              <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-[#D98A94] fill-[#D98A94]/40" />
              <span className="h-px w-10 sm:w-16 bg-gradient-to-l from-transparent to-[#D98A94]/50" />
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.55 }}
                className="feature-card group relative rounded-3xl p-6 sm:p-8 text-center transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(253,244,239,0.85) 100%)',
                  border: '1px solid rgba(217,138,148,0.18)',
                  boxShadow: '0 6px 20px -6px rgba(217,138,148,0.12)',
                }}
              >
                {/* Soft romantic blush glow — top-right corner */}
                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#D98A94]/12 blur-3xl group-hover:bg-[#D98A94]/20 transition-colors" />
                {/* Accent corner heart */}
                <Heart className="absolute top-4 right-4 w-3.5 h-3.5 text-[#D98A94]/25 fill-[#D98A94]/15" aria-hidden="true" />

                <div className="relative z-10 flex flex-col items-center">
                  {/* Icon in gradient pill */}
                  <div
                    className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full flex items-center justify-center mb-5 sm:mb-6 shadow-md group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300"
                    style={{
                      background: 'linear-gradient(135deg, #D98A94 0%, #C7867A 100%)',
                      boxShadow: '0 8px 20px -6px rgba(217,138,148,0.45)',
                    }}
                  >
                    <f.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={2} />
                  </div>

                  <h3 className="text-lg sm:text-xl font-semibold t-text mb-2 sm:mb-3 font-romantic">{f.title}</h3>

                  {/* Mini underline */}
                  <span className="block h-[2px] w-8 bg-gradient-to-r from-transparent via-[#D98A94]/60 to-transparent mb-3 sm:mb-4" aria-hidden="true" />

                  <p className="t-text-secondary text-sm font-body leading-relaxed max-w-[240px]">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live public world map + counter */}
      <WorldMap />

      {/* Terms & Conditions Section — public, clickable */}
      <section id="terms" className="py-14 sm:py-20 px-4 sm:px-6 relative" style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full glass mb-4 sm:mb-6">
              <ScrollText className="w-4 h-4 text-[#D98A94]" />
              <span className="text-xs sm:text-sm t-text-secondary font-body tracking-wide">
                {lang === 'en' ? 'Legal & Transparency' : 'Legalitas & Transparansi'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold t-text mb-3 sm:mb-4 tracking-tight font-romantic">
              {lang === 'en' ? 'Terms and Conditions' : 'Syarat dan Ketentuan'}
            </h2>
            <p className="t-text-secondary text-sm sm:text-base font-body max-w-2xl mx-auto leading-relaxed mb-6 sm:mb-8">
              {lang === 'en'
                ? 'Read our full Terms & Conditions — data policy, auto-publication on social media, sensitive data we never request, your right to withdraw consent, and full legal basis under UU PDP (Law 27/2022) and UU ITE.'
                : 'Baca lengkap Syarat & Ketentuan kami — kebijakan data, publikasi otomatis di media sosial, data sensitif yang tidak pernah kami minta, hak Anda menarik persetujuan, serta dasar hukum lengkap berdasarkan UU PDP (UU 27/2022) dan UU ITE.'}
            </p>
            <button
              type="button"
              onClick={() => setShowTnc(true)}
              data-testid="landing-tnc-btn"
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white font-medium text-sm sm:text-base btn-glow transition active:scale-95"
            >
              <FileText className="w-4 h-4" />
              {lang === 'en' ? 'Read Terms & Conditions' : 'Baca Syarat & Ketentuan'}
              <ChevronRight className="w-4 h-4" />
            </button>
            <p className="text-[11px] sm:text-xs t-text-muted mt-4 sm:mt-6">
              {lang === 'en'
                ? 'Last updated: June 2026'
                : 'Terakhir diperbarui: Juni 2026'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 sm:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-20">
            <span className="text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#C7867A] font-medium">{lang === 'en' ? '18 Unique Communities' : '18 Komunitas Unik'}</span>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold t-text mt-3 sm:mt-4 tracking-tight font-romantic">{t('categories')}</h2>
            <p className="t-text-muted mt-3 sm:mt-4 max-w-xl mx-auto text-sm sm:text-base font-body">
              {lang === 'en' ? 'From faith-based communities to Indonesian diaspora worldwide' : 'Dari komunitas berbasis iman hingga diaspora Indonesia di seluruh dunia'}
            </p>
          </motion.div>

          <div>
            {(() => {
              const groups = { large: [], medium: [], regular: [], dev: [] };
              CATEGORIES_SORTED.forEach(c => { groups[categoryTier(c)].push(c); });
              const tierConfig = {
                large: {
                  grid: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
                  gap: 'gap-5',
                  padding: 'p-6 sm:p-8',
                  titleSize: 'text-xl sm:text-2xl',
                  titleMinH: 'min-h-[64px] sm:min-h-[72px]',
                  descSize: 'text-sm',
                  descMinH: 'min-h-[40px]',
                  tiktokSize: 'w-9 h-9',
                  handleSize: 'text-xs',
                },
                medium: {
                  grid: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
                  gap: 'gap-4',
                  padding: 'p-4 sm:p-5',
                  titleSize: 'text-sm sm:text-base',
                  titleMinH: 'min-h-[40px]',
                  descSize: 'text-xs',
                  descMinH: 'min-h-[52px]',
                  tiktokSize: 'w-7 h-7',
                  handleSize: 'text-[11px]',
                },
                regular: {
                  grid: 'grid-cols-2 sm:grid-cols-3',
                  gap: 'gap-4',
                  padding: 'p-4 sm:p-5',
                  titleSize: 'text-sm sm:text-base',
                  titleMinH: 'min-h-[24px]',
                  descSize: 'text-xs',
                  descMinH: 'min-h-[52px]',
                  tiktokSize: 'w-7 h-7',
                  handleSize: 'text-[11px]',
                },
                dev: {
                  grid: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-7',
                  gap: 'gap-3',
                  padding: 'p-3',
                  titleSize: 'text-xs sm:text-sm',
                  titleMinH: '',
                  descSize: 'text-[10px]',
                  descMinH: '',
                  tiktokSize: 'w-6 h-6',
                  handleSize: '',
                },
              };
              const tierOrder = ['large', 'medium', 'regular', 'dev'];
              return tierOrder.map(tier => {
                const list = groups[tier];
                if (list.length === 0) return null;
                const cfg = tierConfig[tier];
                const isDev = tier === 'dev';
                return (
                  <React.Fragment key={tier}>
                    {isDev && list.length > 0 && (
                      <div className="flex items-center gap-3 mt-8 mb-4">
                        <div className="flex-1 h-px bg-[var(--border)]" />
                        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold t-text-muted">
                          {lang === 'en' ? 'Under Development' : 'Sedang Dikembangkan'}
                        </span>
                        <div className="flex-1 h-px bg-[var(--border)]" />
                      </div>
                    )}
                    <div className={`grid ${cfg.grid} ${cfg.gap} ${isDev ? '' : 'mb-6'}`}>
                      {list.map((cat, i) => {
                        const tiktokUrl = cat.tiktok ? `https://www.tiktok.com/@${cat.tiktok}` : '';
                        if (isDev) {
                          return (
                            <motion.div
                              key={cat.name}
                              initial={{ opacity: 0, y: 8 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ delay: i * 0.02 }}
                              onClick={() => navigate(`/kategori/${catSlug(cat.name)}`)}
                              data-testid={`category-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                              className={`glass-card rounded-xl ${cfg.padding} cursor-pointer transition-all duration-200 group flex items-center gap-2 opacity-60 hover:opacity-100`}
                            >
                              <span
                                aria-hidden
                                data-testid={`tiktok-dev-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                                className="w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0"
                                style={{ backgroundColor: cat.color, opacity: 0.5 }}
                              />
                              <span className={`${cfg.titleSize} font-semibold t-text truncate flex-1`}>
                                {cat.name}
                              </span>
                            </motion.div>
                          );
                        }
                        return (
                          <motion.div
                            key={cat.name}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.03 }}
                            onMouseEnter={() => setHoveredCat(cat.name)}
                            onMouseLeave={() => setHoveredCat(null)}
                            onClick={() => navigate(`/kategori/${catSlug(cat.name)}`)}
                            data-testid={`category-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                            className={`glass-card rounded-2xl ${cfg.padding} cursor-pointer transition-all duration-300 group relative overflow-hidden flex flex-col h-full`}
                            style={{ borderColor: hoveredCat === cat.name ? `${cat.color}55` : undefined }}
                          >
                            <div
                              className="absolute top-0 left-0 w-full h-[3px] transition-all duration-300"
                              style={{ backgroundColor: cat.color, opacity: hoveredCat === cat.name ? 1 : 0.55 }}
                            />
                            <div className={`flex items-start justify-between gap-2 ${cfg.titleMinH}`}>
                              <h3
                                className={`${cfg.titleSize} font-semibold t-text leading-tight tracking-tight`}
                                style={{ hyphens: 'none' }}
                              >
                                {cat.name}
                              </h3>
                              <a
                                href={tiktokUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                aria-label={`TikTok ${cat.name}`}
                                title={`@${cat.tiktok}`}
                                data-testid={`tiktok-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                                className={`${cfg.tiktokSize} flex-shrink-0 rounded-full bg-black hover:bg-[#EE1D52] transition-colors flex items-center justify-center text-white shadow-sm`}
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-[50%] h-[50%]">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.26a8.16 8.16 0 0 0 4.77 1.52V6.33a4.85 4.85 0 0 1-1.84-.36z"/>
                                </svg>
                              </a>
                            </div>
                            <p
                              className={`${cfg.descSize} t-text-muted font-body leading-relaxed mt-2 ${cfg.descMinH}`}
                              style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                              {lang === 'en' ? cat.en : cat.id}
                            </p>
                            <div className="mt-auto pt-3 flex items-center justify-between">
                              <a
                                href={tiktokUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className={`${cfg.handleSize} font-semibold text-[#D98A94] hover:underline truncate`}
                              >
                                @{cat.tiktok}
                              </a>
                              <ChevronRight className="w-3.5 h-3.5 t-text-muted group-hover:translate-x-1 transition-transform flex-shrink-0" />
                            </div>
                            <div
                              className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full transition-opacity duration-300 pointer-events-none"
                              style={{ backgroundColor: cat.color, opacity: hoveredCat === cat.name ? 0.10 : 0, filter: 'blur(24px)' }}
                            />
                          </motion.div>
                        );
                      })}
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* CTA Section with illustrations */}
      <section className="py-16 sm:py-32 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] rounded-full bg-[#D98A94]/5 blur-[150px]" />
          <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] rounded-full bg-[#C7867A]/5 blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-8 sm:gap-16">
          {/* Illustration */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="flex-shrink-0 flex gap-3 sm:gap-4">
            <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl w-32 sm:w-48 h-40 sm:h-60">
              {WOMAN_SVG}
            </div>
            <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl mt-6 sm:mt-8 w-32 sm:w-48 h-40 sm:h-60">
              {MAN_SVG}
            </div>
          </motion.div>

          {/* Text */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="text-center lg:text-left">
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold t-text tracking-tight mb-4 sm:mb-6 font-romantic">
              {lang === 'en' ? 'Ready to Find Your Match?' : 'Siap Menemukan Pasanganmu?'}
            </h2>
            <p className="t-text-secondary text-sm sm:text-lg mb-6 sm:mb-10 font-body max-w-xl">
              {lang === 'en'
                ? 'Explore profiles from across Indonesia and beyond. The person who feels like home might already be here — waiting quietly for you.'
                : 'Jelajahi profil dari seluruh Indonesia dan luar negeri. Seseorang yang terasa seperti rumah mungkin sudah menunggumu di sini — dengan tenang.'}
            </p>
            <Button data-testid="cta-get-started-btn" onClick={() => navigate('/auth')}
              className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full px-8 sm:px-12 py-5 sm:py-7 text-base sm:text-xl font-medium btn-glow active:scale-95" size="lg">
              <Heart className="w-5 sm:w-6 h-5 sm:h-6 mr-2 sm:mr-3" fill="white" />{t('get_started')}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Category Detail Dialog */}
      <Dialog open={!!selectedCat} onOpenChange={(o) => { if (!o) setSelectedCat(null); }}>
        <DialogContent className="glass-card t-text max-w-md">
          {selectedCat && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: selectedCat.color }} />
                  <span className="t-text text-lg">{selectedCat.name}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <p className="t-text-secondary text-sm leading-relaxed">
                  {lang === 'en' ? selectedCat.detail_en : selectedCat.detail_id}
                </p>
                <Button onClick={() => { setSelectedCat(null); navigate('/auth'); }}
                  className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="cat-join-btn">
                  <ArrowRight className="w-4 h-4 mr-2" />{lang === 'en' ? 'Join Now' : 'Gabung Sekarang'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Ticketing System — public, at bottom of landing page (before footer) */}
      <TicketSupport />

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6" style={{ borderTop: '1px solid var(--border-color)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start justify-between gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/images/mgi-logo.jpeg" alt="MGI" className="w-6 h-6 sm:w-7 sm:h-7 rounded-md object-contain" />
                <span className="text-base sm:text-xl font-bold t-text">Match Group <span className="gradient-text">ID</span></span>
              </div>
              <p className="t-text-muted text-sm font-body max-w-sm">
                {lang === 'en' ? 'Indonesia\'s premier matchmaking platform connecting hearts across cultures, faiths, and borders.' : 'Platform perjodohan terbaik Indonesia yang menghubungkan hati lintas budaya, iman, dan batas negara.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              {CATEGORIES.slice(0, 10).map(c => (
                <span key={c.name} className="text-xs t-text-muted hover:text-[#D98A94] cursor-pointer transition-colors">{c.name}</span>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '2rem' }}>
            <div className="text-sm t-text-muted text-center sm:text-left">
              <p>2026 Match Group Indonesia. All rights reserved.</p>
              <p className="mt-1">
                <a href="https://wa.me/6281318719105?text=Halo%20MGI%2C%20saya%20ingin%20join%20member" target="_blank" rel="noreferrer" data-testid="footer-join-wa"
                  className="inline-flex items-center gap-1.5 text-[#25D366] hover:opacity-80 font-semibold">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" /></svg>
                  Join Member · WA 0813 1871 9105
                </a>
              </p>
            </div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <button onClick={() => setShowTnc(true)} data-testid="footer-tnc-link" className="t-text-muted hover:text-[#D98A94] transition text-sm underline underline-offset-4">
                {lang === 'en' ? 'Terms & Conditions' : 'Syarat & Ketentuan'}
              </button>
              <button onClick={toggleTheme} className="t-text-muted hover:opacity-80 transition text-sm">
                {theme === 'dark' ? t('light_mode') : t('dark_mode')}
              </button>
              <button onClick={toggleLang} className="t-text-muted hover:opacity-80 transition text-sm">
                {lang === 'en' ? 'Bahasa Indonesia' : 'English'}
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Public Terms & Conditions Modal — accessible from landing section + footer */}
      <Dialog open={showTnc} onOpenChange={setShowTnc}>
        <DialogContent className="glass-card t-text max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tnc-dialog">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-[#D98A94]" />
              {lang === 'en' ? 'Terms and Conditions' : 'Syarat dan Ketentuan'}
            </DialogTitle>
            <DialogDescription className="t-text-muted text-xs">
              {lang === 'en'
                ? 'Full formal text — please read carefully.'
                : 'Naskah formal lengkap — mohon dibaca dengan seksama.'}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <TncBody lang={lang} />
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <a
              href="/syarat-ketentuan"
              target="_blank"
              rel="noreferrer"
              data-testid="tnc-dialog-open-page"
              className="flex-1 text-center px-4 py-2 rounded-full glass-card border border-[#D98A94]/40 text-xs sm:text-sm t-text-secondary hover:text-[#D98A94] transition"
            >
              {lang === 'en' ? 'Open in New Tab' : 'Buka di Tab Baru'}
            </a>
            <Button onClick={() => setShowTnc(false)} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="tnc-dialog-close">
              {lang === 'en' ? 'Close' : 'Tutup'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
